/**
 * Per-message thumbs up/down feedback.
 */

import { Icons } from './utils/icons.js';
import { t } from '../../i18n.js';

/**
 * @param {Object} options
 * @param {string} options.messageId
 * @param {1|-1|null} options.rating
 * @param {function(string, 1|-1, string=): void} options.onSubmit
 * @returns {HTMLElement}
 */
export function createMessageFeedback({ messageId, rating, onSubmit }) {
  const wrap = document.createElement('div');
  wrap.className = 'ai-message-feedback';

  let notePopover = null;
  let removeDocListener = null;

  function closeNotePopover() {
    if (removeDocListener) {
      removeDocListener();
      removeDocListener = null;
    }
    if (notePopover) {
      notePopover.remove();
      notePopover = null;
    }
  }

  function openNotePopover(anchor) {
    closeNotePopover();

    const popover = document.createElement('div');
    popover.className = 'ai-feedback-note-popover';
    popover.setAttribute('role', 'dialog');

    const prompt = document.createElement('p');
    prompt.className = 'ai-feedback-note-popover__prompt';
    prompt.textContent = t('ai_feedback_note_prompt');

    const textarea = document.createElement('textarea');
    textarea.className = 'ai-feedback-note-popover__input';
    textarea.rows = 3;
    textarea.maxLength = 1000;
    textarea.placeholder = t('ai_feedback_note_placeholder');

    const actions = document.createElement('div');
    actions.className = 'ai-feedback-note-popover__actions';

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'ai-feedback-note-popover__btn secondary';
    skipBtn.textContent = t('ai_feedback_skip');
    skipBtn.onclick = () => {
      onSubmit(messageId, -1);
      closeNotePopover();
      updateButtons(-1);
    };

    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className = 'ai-feedback-note-popover__btn primary';
    submitBtn.textContent = t('ai_feedback_submit');
    submitBtn.onclick = () => {
      const note = textarea.value.trim();
      onSubmit(messageId, -1, note || undefined);
      closeNotePopover();
      updateButtons(-1);
    };

    actions.appendChild(skipBtn);
    actions.appendChild(submitBtn);
    popover.appendChild(prompt);
    popover.appendChild(textarea);
    popover.appendChild(actions);

    document.body.appendChild(popover);
    notePopover = popover;

    const rect = anchor.getBoundingClientRect();
    const popoverHeight = popover.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const fitsBelow = spaceBelow >= popoverHeight;

    if (fitsBelow) {
      popover.style.top = `${rect.bottom + 6}px`;
    } else {
      popover.style.top = `${Math.max(8, rect.top - popoverHeight - 6)}px`;
    }
    popover.style.left = `${Math.max(8, Math.min(rect.left - 180, window.innerWidth - 268))}px`;
    textarea.focus();

    const onDocClick = (e) => {
      if (!popover.contains(e.target) && e.target !== anchor) {
        closeNotePopover();
      }
    };
    removeDocListener = () => document.removeEventListener('click', onDocClick, true);
    requestAnimationFrame(() => document.addEventListener('click', onDocClick, true));
  }

  const upBtn = document.createElement('button');
  upBtn.type = 'button';
  upBtn.className = 'ai-feedback-btn';
  upBtn.setAttribute('aria-label', t('ai_feedback_thumbs_up'));

  const downBtn = document.createElement('button');
  downBtn.type = 'button';
  downBtn.className = 'ai-feedback-btn';
  downBtn.setAttribute('aria-label', t('ai_feedback_thumbs_down'));

  function updateButtons(nextRating) {
    upBtn.innerHTML = nextRating === 1 ? Icons.thumbsUpFilled() : Icons.thumbsUp();
    downBtn.innerHTML = nextRating === -1 ? Icons.thumbsDownFilled() : Icons.thumbsDown();
    upBtn.classList.toggle('is-active', nextRating === 1);
    downBtn.classList.toggle('is-active', nextRating === -1);
    downBtn.classList.toggle('is-negative', nextRating === -1);
    wrap.classList.toggle('has-rating', nextRating != null);
  }

  upBtn.onclick = () => {
    closeNotePopover();
    onSubmit(messageId, 1);
    updateButtons(1);
  };

  downBtn.onclick = () => {
    if (rating === -1) {
      onSubmit(messageId, -1);
      return;
    }
    openNotePopover(downBtn);
  };

  updateButtons(rating);
  wrap.appendChild(upBtn);
  wrap.appendChild(downBtn);

  return wrap;
}
